import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useRealtimeDraft from '../realtime/useRealtimeDraft';
import { Form, Button, Row, Col, Table, Card, Accordion, Alert, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectGeneralProject, selectSoldToShipTo, selectShipEarlyParent } from '../redux/selectors';
import { selectUserRoles } from '../redux/slices/userSlice';
import { selectApiUrls } from '../redux/slices/apiUrlSlice';
import { isDevelopmentOrUAT } from '../redux/slices/environmentSlice';
import { mapUserRoles } from '../functions/useRoleMapping';
import axiosInstance from '../axios/axiosConfig';
import SaveBanner from './JobDetailPage/Mechanical/SaveBanner';
import useSendEmail from '../functions/useSendEmail';
import useUserContact from '../functions/useUserContact';
import { getUserEmailByRole, extractEmailsFromProjectTeam } from '../functions/getUserEmailByRole';
import { usePermissions } from '../hooks/usePermissions';
import { formatDateForAPI } from '../utils/dateUtils';
// Import PDF generation utilities
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../styles.css';
import './ShipEarlyForm.css';
import { postChangeLog } from '../redux/slices/postChangeLogSlice'; // Import postChangeLog
import { postData } from '../redux/slices/apiCallSlice';
import { useDispatch } from 'react-redux';

const DEFAULT_EMAIL_RECIPIENTS = [
  'david.neesen@watertronics.com',
  'mark.hubbard@watertronics.com',
  'chandler.blaschka@watertronics.com',
  'zack.sukys@watertronics.com'
];

function ShipEarlyForm() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formId, setFormId] = useState(null);
  const [hasExistingParent, setHasExistingParent] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  
  // New state for managing submitted forms list
  const [submittedForms, setSubmittedForms] = useState([]);
  const [showNewForm, setShowNewForm] = useState(false);
  const [expandedFormId, setExpandedFormId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFormId, setEditingFormId] = useState(null);
  const [editingFormData, setEditingFormData] = useState(null);
  const [editingItems, setEditingItems] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  useRealtimeDraft(showNewForm || showEditModal || savingEdit || loading);
  const [shipEarlyScheduleRows, setShipEarlyScheduleRows] = useState([]);
  
  // New state for managing address copying
  const [, setCopyAddresses] = useState(true);
  
  // Get data from Redux using selectors with useMemo to prevent dependency issues
  const generalProjectDataRaw = useSelector(selectGeneralProject);
  const generalProjectData = useMemo(() => generalProjectDataRaw[0] || {}, [generalProjectDataRaw]);
  const currentUser = useSelector((state) => state.user?.userDetails?.name || '');
  const currentUserEmail = useSelector(
    (state) => state.user?.userDetails?.username || state.user?.userDetails?.email || ''
  );
  const restrictEmailToCurrentUser = useSelector(isDevelopmentOrUAT);
  const apiUrls = useSelector(selectApiUrls);
  const jobId = useSelector((state) => state.jobId.jobId);
  
  // Get soldToShipTo data from Redux
  const storedSoldToShipTo = useSelector(selectSoldToShipTo);
  const soldToShipToData = useMemo(() => storedSoldToShipTo[0] || {}, [storedSoldToShipTo]);
  
  // Get ship early data from Redux (already loaded by fetchFullJob)
  // ShipEarlyItems are now nested within ShipEarlyParent objects
  const shipEarlyParentData = useSelector(selectShipEarlyParent);
  
  // Get user roles and map them
  const userRoles = useSelector(selectUserRoles);
  const [roles, setRoles] = useState(mapUserRoles(userRoles));
  const { canEdit } = usePermissions();
  
  // Update roles when userRoles change
  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);
  
  // Check if user can manage ship early forms based on component policies.
  const hasPermission = canEdit('portal.ship-early.manage');
  
  // Initialize email hook
  const sendEmail = useSendEmail();

  const formatShipTypeForStorage = useCallback((shipType) => {
    switch (shipType) {
      case 'shipEarly':
        return 'Ship Early';
      case 'shipDirect':
        return 'Ship Direct from Vendor';
      case 'both':
        return 'Both Ship Early and Direct from Vendor';
      default:
        return shipType;
    }
  }, []);

  const formatShipTypeForDisplay = useCallback((shipType) => {
    const normalized = String(shipType || '').trim().toLowerCase();

    if (!normalized) return 'N/A';
    if (normalized === 'ship early' || normalized === 'shipearly') return 'Ship Early';
    if (
      normalized === 'shipdirect' ||
      normalized === 'ship direct' ||
      normalized === 'direct' ||
      normalized === 'ship direct from vendor' ||
      normalized === 'both' ||
      normalized === 'both ship early and direct from vendor' ||
      normalized === 'ship early & direct'
    ) {
      return 'Ship Early & Direct';
    }

    return shipType;
  }, []);

  const normalizeShipTypeForForm = useCallback((shipType) => {
    if (!shipType) return '';
    const normalized = String(shipType).trim().toLowerCase();

    if (normalized === 'ship early' || normalized === 'shipearly') return 'shipEarly';
    if (
      normalized === 'ship direct from vendor' ||
      normalized === 'shipdirect' ||
      normalized === 'ship direct' ||
      normalized === 'direct' ||
      normalized === 'ship early & direct'
    ) return 'shipDirect';
    if (normalized === 'both ship early and direct from vendor' || normalized === 'both') return 'shipDirect';

    return shipType;
  }, []);

  const buildScheduleModelNumberForItem = useCallback((item) => {
    const shipType = formatShipTypeForStorage(normalizeShipTypeForForm(item.shipType || item.ShipType || ''));
    const title = item.title || item.Title || '';
    const quantity = item.quantity || item.Quantity || '';

    return `${shipType} - ${title} (${quantity})`;
  }, [formatShipTypeForStorage, normalizeShipTypeForForm]);

  const getItemJobStatus = useCallback((item, form) => {
    const directStatus = item.jobStatus || item.JobStatus;
    if (directStatus) {
      return directStatus;
    }

    const jobNumber = form.JobNumber || form.jobNumber || form.jobNum || generalProjectData.JobNumber;
    const modelNumber = buildScheduleModelNumberForItem(item);
    const matchingScheduleRow = shipEarlyScheduleRows.find((row) => (
      String(row.JobNumber || '') === String(jobNumber || '') &&
      String(row.ModelNumber || '') === modelNumber
    ));

    return matchingScheduleRow?.JobStatus || 'N/A';
  }, [buildScheduleModelNumberForItem, generalProjectData.JobNumber, shipEarlyScheduleRows]);
  
  // State for form data
  const [formData, setFormData] = useState({
    jobName: '',
    jobNum: '',
    formAuthor: '',
    billingType: '', // Changed to single billingType field
    invoiceNumber: '',
    customerName: '',
    customerAddress: '',
    customerCity: '',
    customerState: '',
    customerZip: '',
    customerContactName: '',
    customerContactPhone: '',
    customerContactEmail: '',
    shipToName: '',
    shipToAddress: '',
    shipToCity: '',
    shipToState: '',
    shipToZip: '',
    shipToContactName: '',
    shipToContactPhone: '',
    shipToContactEmail: '',
    costAssociated: ''
  });  // State for shipment items
  const [shipmentItems, setShipmentItems] = useState([]);
  // State for form submission and disabling
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Get user contacts and project team members
  const { userContacts, loading: contactsLoading, error: contactsError } = useUserContact();
  const [projectTeamMembers, setProjectTeamMembers] = useState([]);
  const emailRecipients = useMemo(() => {
    if (restrictEmailToCurrentUser) {
      return currentUserEmail ? [currentUserEmail] : [];
    }

    return [
      ...DEFAULT_EMAIL_RECIPIENTS,
      ...extractEmailsFromProjectTeam(projectTeamMembers)
    ];
  }, [currentUserEmail, projectTeamMembers, restrictEmailToCurrentUser]);
  
  // Check for existing ship early parents and load all submitted forms
  // Refactored to use Redux data instead of API calls (data already loaded by fetchFullJob)
  const checkExistingParent = useCallback(() => {
    try {
      setInitialLoading(true);
      setError(null);
      
      if (!jobId) {
        setHasExistingParent(false);
        setSubmittedForms([]);
        setInitialLoading(false);
        return;
      }
      
      // Use data from Redux (already loaded by fetchFullJob in JobHandler)
      if (shipEarlyParentData && shipEarlyParentData.length > 0) {
        // Filter records that match the jobId
        const matchingRecords = shipEarlyParentData.filter(record => 
          String(record.ProjectID) === String(jobId)
        );
        
        if (matchingRecords.length > 0) {
          setHasExistingParent(true);
          
          // Map parent records with their nested items
          const formsWithItems = matchingRecords.map((record) => {
            // Items are now nested directly within the parent object as ShipEarlyItems array
            const nestedItems = record.ShipEarlyItems || [];
            
            const items = nestedItems.map(item => ({
              id: item.ID || item.id,
              title: item.Title || item.title || '',
              description: item.Description || item.description || '',
              quantity: item.Quantity || item.quantity || '',
              shipType: normalizeShipTypeForForm(item.ShipType || item.shipType || ''),
              jobStatus: item.JobStatus || item.jobStatus || '',
              requestedDeliveryDate: (item.RequestedDeliveryDate || item.requestedDeliveryDate) 
                ? new Date(item.RequestedDeliveryDate || item.requestedDeliveryDate).toISOString().split('T')[0] 
                : ''
            }));
            
            return {
              ...record,
              items,
              createdDate: record.CreatedDate || record.createdDate || new Date().toISOString()
            };
          });
          
          // Sort forms by creation date (newest first)
          formsWithItems.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
          setSubmittedForms(formsWithItems);
        } else {
          setHasExistingParent(false);
          setSubmittedForms([]);
        }
      } else {
        setHasExistingParent(false);
        setSubmittedForms([]);
      }
    } catch (err) {
      console.error('Error processing ship early data from Redux:', err);
      setError('Failed to load existing forms. Please try again later.');
    } finally {
      setInitialLoading(false);
    }
  }, [jobId, shipEarlyParentData, normalizeShipTypeForForm]);  // Create a new ship early form

  useEffect(() => {
    const fetchShipEarlyScheduleRows = async () => {
      if (!apiUrls.generalProject || submittedForms.length === 0) {
        setShipEarlyScheduleRows([]);
        return;
      }

      const jobNumbers = new Set(
        submittedForms
          .map((form) => form.JobNumber || form.jobNumber || generalProjectData.JobNumber)
          .filter(Boolean)
          .map(String)
      );

      if (jobNumbers.size === 0) {
        setShipEarlyScheduleRows([]);
        return;
      }

      try {
        const response = await axiosInstance.get(apiUrls.generalProject);
        const rows = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
            ? response.data
            : [];

        setShipEarlyScheduleRows(rows.filter((row) => {
          const modelNumber = String(row.ModelNumber || '');
          const isShipEarlyScheduleRow =
            row.ScheduleOnly === true ||
            String(row.ScheduleOnly || '').toLowerCase() === 'yes';

          return (
            jobNumbers.has(String(row.JobNumber || '')) &&
            isShipEarlyScheduleRow &&
            (modelNumber.includes('Ship Early') || modelNumber.includes('Direct from Vendor'))
          );
        }));
      } catch (err) {
        console.error('Failed to load Ship Early schedule statuses:', err);
        setShipEarlyScheduleRows([]);
      }
    };

    fetchShipEarlyScheduleRows();
  }, [apiUrls.generalProject, generalProjectData.JobNumber, submittedForms]);
  const createNewShipEarlyForm = () => {
    setShowNewForm(true);
    setFormId(null);
    setFormSubmitted(false);
    setCopyAddresses(true); // Reset to default checked state
    
    // Initialize form with project data and addresses if copyAddresses is true
    const initialFormData = {
      jobName: generalProjectData.ProjectName || '',
      jobNum: generalProjectData.JobNumber || '',
      formAuthor: currentUser || '',
      billingType: '',
      invoiceNumber: '',
      customerName: soldToShipToData.SoldToCompanyName || '',
      customerAddress: soldToShipToData.SoldToAddress || '',
      customerCity: soldToShipToData.SoldToCity || '',
      customerState: soldToShipToData.SoldToState || '',
      customerZip: soldToShipToData.SoldToZipCode || '',
      customerContactName: `${soldToShipToData.SoldToFirstName || ''} ${soldToShipToData.SoldToLastName || ''}`.trim(),
      customerContactPhone: soldToShipToData.SoldToPhone || '',
      customerContactEmail: soldToShipToData.SoldToEmail || '',
      shipToName: soldToShipToData.ShipToCompanyName || '',
      shipToAddress: soldToShipToData.ShipToAddress || '',
      shipToCity: soldToShipToData.ShipToCity || '',
      shipToState: soldToShipToData.ShipToState || '',
      shipToZip: soldToShipToData.ShipToZipCode || '',
      shipToContactName: `${soldToShipToData.ShipToFirstName || ''} ${soldToShipToData.ShipToLastName || ''}`.trim(),
      shipToContactPhone: soldToShipToData.ShipToPhone || '',
      shipToContactEmail: soldToShipToData.ShipToEmail || '',
      costAssociated: ''
    };
    
    setFormData(initialFormData);
    setShipmentItems([]);
  };
  
  // Toggle expanded view of a submitted form
  const toggleFormExpansion = (formId) => {
    setExpandedFormId(expandedFormId === formId ? null : formId);
  };

  const handleOpenEditModal = (form) => {
    const normalizedFormData = {
      jobName: form.JobName || form.jobName || '',
      jobNum: form.JobNumber || form.jobNumber || '',
      formAuthor: form.WrittenBy || form.writtenBy || '',
      billingType: form.BillingType || form.billingType || '',
      invoiceNumber: form.InvoiceNumber || form.invoiceNumber || '',
      customerName: form.BillToCompany || form.billToCompany || '',
      customerAddress: form.BillToAddress || form.billToAddress || '',
      customerCity: form.BillToCity || form.billToCity || '',
      customerState: form.BillToState || form.billToState || '',
      customerZip: form.BillToZip || form.billToZip || '',
      customerContactName: form.BillToName || form.billToName || '',
      customerContactPhone: form.BillToPhone || form.billToPhone || '',
      customerContactEmail: form.BillToEmail || form.billToEmail || '',
      shipToName: form.ShipToCompany || form.shipToCompany || '',
      shipToAddress: form.ShipToAddress || form.shipToAddress || '',
      shipToCity: form.ShipToCity || form.shipToCity || '',
      shipToState: form.ShipToState || form.shipToState || '',
      shipToZip: form.ShipToZip || form.shipToZip || '',
      shipToContactName: form.ShipToName || form.shipToName || '',
      shipToContactPhone: form.ShipToPhone || form.shipToPhone || '',
      shipToContactEmail: form.ShipToEmail || form.shipToEmail || '',
      costAssociated: form.ShipmentCost || form.shipmentCost || ''
    };

    const normalizedItems = (form.items || []).map((item) => ({
      id: item.id,
      title: item.title || '',
      description: item.description || '',
      quantity: item.quantity || '',
      shipType: normalizeShipTypeForForm(item.shipType || ''),
      jobStatus: item.jobStatus || item.JobStatus || '',
      requestedDeliveryDate: item.requestedDeliveryDate || '',
      isNew: false
    }));

    setEditingFormId(form.ID || form.id);
    setEditingFormData(normalizedFormData);
    setEditingItems(normalizedItems);
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    if (savingEdit) return;
    setShowEditModal(false);
    setEditingFormId(null);
    setEditingFormData(null);
    setEditingItems([]);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditingFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditBillingTypeChange = (type) => {
    setEditingFormData((prev) => ({
      ...prev,
      billingType: type,
      invoiceNumber: type === 'Job Number' ? '' : prev.invoiceNumber
    }));
  };

  const handleEditItemChange = (id, field, value) => {
    setEditingItems((prev) => prev.map((item) => (
      item.id === id ? { ...item, [field]: value } : item
    )));
  };

  const handleAddEditItem = () => {
    const newId = Date.now();
    setEditingItems((prev) => ([
      ...prev,
      {
        id: newId,
        title: '',
        description: '',
        quantity: '',
        shipType: '',
        jobStatus: 'Ready to Schedule',
        requestedDeliveryDate: '',
        isNew: true
      }
    ]));
  };

  const handleRemoveEditItem = (id) => {
    setEditingItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSaveEditedForm = async () => {
    if (!hasPermission) return;
    if (!editingFormId || !editingFormData) return;

    try {
      setSavingEdit(true);

      const parsedShipmentCost =
        editingFormData.costAssociated === '' || editingFormData.costAssociated === null || editingFormData.costAssociated === undefined
          ? null
          : Number(editingFormData.costAssociated);

      const shipmentCostValue = Number.isNaN(parsedShipmentCost) ? null : parsedShipmentCost;

      const parentData = {
        ID: editingFormId,
        ProjectID: parseInt(jobId, 10) || null,
        JobName: editingFormData.jobName,
        JobNumber: editingFormData.jobNum,
        WrittenBy: editingFormData.formAuthor,
        ShipmentCost: shipmentCostValue,
        BillingType: editingFormData.billingType,
        InvoiceNumber: editingFormData.billingType === 'Job Number' ? editingFormData.jobNum : editingFormData.invoiceNumber,
        BillToCompany: editingFormData.customerName,
        BillToAddress: editingFormData.customerAddress,
        BillToCity: editingFormData.customerCity,
        BillToState: editingFormData.customerState,
        BillToZip: editingFormData.customerZip,
        BillToName: editingFormData.customerContactName,
        BillToPhone: editingFormData.customerContactPhone,
        BillToEmail: editingFormData.customerContactEmail,
        ShipToCompany: editingFormData.shipToName,
        ShipToAddress: editingFormData.shipToAddress,
        ShipToCity: editingFormData.shipToCity,
        ShipToState: editingFormData.shipToState,
        ShipToZip: editingFormData.shipToZip,
        ShipToName: editingFormData.shipToContactName,
        ShipToPhone: editingFormData.shipToContactPhone,
        ShipToEmail: editingFormData.shipToContactEmail
      };

      await axiosInstance.patch(apiUrls.shipEarlyParent, parentData);

      for (const item of editingItems) {
        const itemData = {
          ParentID: editingFormId,
          Title: item.title,
          Description: item.description,
          Quantity: item.quantity,
          ShipType: formatShipTypeForStorage(item.shipType),
          RequestedDeliveryDate: item.requestedDeliveryDate
        };

        if (item.isNew) {
          await axiosInstance.post(apiUrls.shipEarlyItems, itemData);
        } else {
          await axiosInstance.put(`${apiUrls.shipEarlyItems}?id=${item.id}`, {
            ID: item.id,
            ...itemData
          });
        }
      }

      const updatedItemsForDisplay = editingItems.map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description,
        quantity: item.quantity,
        shipType: formatShipTypeForStorage(item.shipType),
        jobStatus: item.jobStatus || 'Ready to Schedule',
        requestedDeliveryDate: item.requestedDeliveryDate
      }));

      setSubmittedForms((prev) => prev.map((form) => {
        const currentId = form.ID || form.id;
        if (String(currentId) !== String(editingFormId)) {
          return form;
        }

        return {
          ...form,
          JobName: editingFormData.jobName,
          jobName: editingFormData.jobName,
          JobNumber: editingFormData.jobNum,
          jobNumber: editingFormData.jobNum,
          WrittenBy: editingFormData.formAuthor,
          writtenBy: editingFormData.formAuthor,
          BillingType: editingFormData.billingType,
          billingType: editingFormData.billingType,
          InvoiceNumber: editingFormData.billingType === 'Job Number' ? editingFormData.jobNum : editingFormData.invoiceNumber,
          invoiceNumber: editingFormData.billingType === 'Job Number' ? editingFormData.jobNum : editingFormData.invoiceNumber,
          BillToCompany: editingFormData.customerName,
          billToCompany: editingFormData.customerName,
          ShipToCompany: editingFormData.shipToName,
          shipToCompany: editingFormData.shipToName,
          ShipmentCost: shipmentCostValue,
          shipmentCost: shipmentCostValue,
          items: updatedItemsForDisplay
        };
      }));

      setSaveStatus('Ship Early form updated successfully.');
      setShowNotification(true);
      setShowEditModal(false);
      setEditingFormId(null);
      setEditingFormData(null);
      setEditingItems([]);

      setTimeout(() => {
        setShowNotification(false);
      }, 4000);
    } catch (editError) {
      console.error('Error updating Ship Early form:', editError);
      setSaveStatus(`Failed to update Ship Early form: ${editError.message}`);
      setShowNotification(true);
    } finally {
      setSavingEdit(false);
    }
  };
    // Cancel new form creation
  const cancelNewForm = () => {
    setShowNewForm(false);
    setFormId(null);
    setFormSubmitted(false);
    setShipmentItems([]);
  };
    // Initial check for existing parent
  useEffect(() => {
    checkExistingParent();
  }, [checkExistingParent]);

  // Initialize form with data from Redux when it's available (removed duplicate effect)
  useEffect(() => {
    setFormData(prevState => ({
      ...prevState,
      jobName: generalProjectData.ProjectName || '',
      jobNum: generalProjectData.JobNumber || '',
      formAuthor: currentUser || '',
    }));
  }, [generalProjectData, currentUser]);

  // Update project team members when user contacts are loaded
  useEffect(() => {
    if (!contactsLoading && !contactsError && userContacts && typeof userContacts === "object" && generalProjectData) {
      const teamMembers = getUserEmailByRole(userContacts, generalProjectData);
      setProjectTeamMembers(teamMembers);
    }
  }, [userContacts, contactsLoading, contactsError, generalProjectData]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };  // Handle field blur - removed PATCH functionality for submit-based approach
  const handleBlur = (e) => {
    // Form is now submit-based, so no auto-saving on blur
    // This function can be used for validation or other client-side operations if needed
    return;
  };
  // Handle radio button billing type changes
  const handleBillingTypeChange = (type) => {
    setFormData({
      ...formData,
      billingType: type,
      // Clear invoice number if switching to Job Number
      invoiceNumber: type === 'Job Number' ? '' : formData.invoiceNumber
    });
  };  // Add new shipment item - refactored to follow submit-based approach
  const handleAddItem = () => {
    // Create a temporary ID for the new item
    const tempId = Date.now();
    
    const newItem = {
      id: tempId,
      title: '',
      description: '',
      quantity: '',
      shipType: '',
      jobStatus: 'Ready to Schedule',
      requestedDeliveryDate: '',
      isNew: true // Mark as new item for submission
    };
    
    setShipmentItems([...shipmentItems, newItem]);
  };
  // Remove shipment item - refactored for submit-based approach
  const handleRemoveItem = async (id) => {
    const itemToDelete = shipmentItems.find(item => item.id === id);
    
    if (!itemToDelete) {
      return;
    }
    
    // Only log if the item has meaningful data (not just added and immediately removed)
    if (itemToDelete.title || itemToDelete.description || itemToDelete.quantity) {
      try {
        // Create a descriptive string for the change log
        const itemDescription = `${itemToDelete.title || "Untitled"}${itemToDelete.quantity ? ` (Quantity: ${itemToDelete.quantity})` : ""}`;
        
        // Post to change log
        await dispatch(postChangeLog({
          projectID: jobId,
          currentUser: currentUser,
          userRole: Object.keys(roles).find(role => roles[role]) || "Unknown",
          section: "Ship Early Form",
          field: itemDescription,
          prevValue: itemDescription,
          newValue: "DELETED",
          dateTime: new Date().toISOString()
        })).unwrap();
      } catch (error) {
        console.error("Failed to log item deletion:", error);
        // Continue with deletion even if logging fails
      }
    }
    
    setShipmentItems(shipmentItems.filter(item => item.id !== id));
  };
  // Update shipment item
  const handleItemChange = (id, field, value) => {
    setShipmentItems(shipmentItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };    // Format form data for API submission (for full form submit)
  const formatFormDataForApi = () => {
    // Format parent data with PascalCase keys for API consistency
    const parentData = {
      ProjectID: parseInt(jobId) || null,
      JobName: formData.jobName,
      JobNumber: formData.jobNum,
      WrittenBy: formData.formAuthor,
      ShipmentCost: formData.costAssociated,
      BillingType: formData.billingType,
      InvoiceNumber: formData.billingType === 'Job Number' ? formData.jobNum : formData.invoiceNumber,
      // Customer/Bill to information
      BillToCompany: formData.customerName,
      BillToAddress: formData.customerAddress,
      BillToCity: formData.customerCity,
      BillToState: formData.customerState,
      BillToZip: formData.customerZip,
      BillToName: formData.customerContactName,
      BillToPhone: formData.customerContactPhone,
      BillToEmail: formData.customerContactEmail,
      // Ship to information
      ShipToCompany: formData.shipToName,
      ShipToAddress: formData.shipToAddress,
      ShipToCity: formData.shipToCity,
      ShipToState: formData.shipToState,
      ShipToZip: formData.shipToZip,
      ShipToName: formData.shipToContactName,
      ShipToPhone: formData.shipToContactPhone,
      ShipToEmail: formData.shipToContactEmail
    };

    // If we're updating an existing form, include the ID
    if (formId) {
      parentData.ID = formId;
    }    
    
    // Format items with consistent ID handling
    const items = shipmentItems.map(item => ({
      ParentID: formId, // This will be updated for new items after parent is created
      Title: item.title,
      Description: item.description,
      Quantity: item.quantity,
      ShipType: item.shipType,
      RequestedDeliveryDate: item.requestedDeliveryDate,
      // Include ID only for existing items (not new ones)
      ...(item.isNew ? {} : { ID: item.id })
    }));    return { parentData, items };
  };

  // Format email body for ship early form notification
  const formatEmailBody = (formData, shipmentItems) => {
    const formatCurrency = (value) => {
      if (!value) return 'N/A';
      const numValue = parseFloat(value);
      return isNaN(numValue) ? 'N/A' : `$${numValue.toFixed(2)}`;
    };

    const getBillingType = () => {
      if (!formData.billingType) return 'N/A';
      if (formData.billingType === 'Job Number') {
        return `Job Number: ${formData.jobNum}`;
      }
      return formData.billingType;
    };

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto;">
        <h2 style="color: #0066cc; border-bottom: 2px solid #0066cc; padding-bottom: 10px;">
          Ship Early Form Submission
        </h2>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
          <h3 style="color: #333; margin-top: 0;">Job Information</h3>
          <p><strong>Job Name:</strong> ${formData.jobName || 'N/A'}</p>
          <p><strong>Job Number:</strong> ${formData.jobNum || 'N/A'}</p>
          <p><strong>Form Author:</strong> ${formData.formAuthor || 'N/A'}</p>
          <p><strong>Cost Associated:</strong> ${formatCurrency(formData.costAssociated)}</p>
          <p><strong>Billing Type:</strong> ${getBillingType()}</p>
          ${formData.invoiceNumber ? `<p><strong>Invoice Number:</strong> ${formData.invoiceNumber}</p>` : ''}
        </div>

        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
          <h3 style="color: #333; margin-top: 0;">Customer Information</h3>
          <div style="display: flex; flex-wrap: wrap; gap: 20px;">
            <div style="flex: 1; min-width: 300px;">
              <h4 style="color: #666;">Bill To:</h4>
              <p><strong>Company:</strong> ${formData.customerName || 'N/A'}</p>
              <p><strong>Address:</strong> ${formData.customerAddress || 'N/A'}</p>
              <p><strong>City, State ZIP:</strong> ${[formData.customerCity, formData.customerState, formData.customerZip].filter(Boolean).join(', ') || 'N/A'}</p>
              <p><strong>Contact:</strong> ${formData.customerContactName || 'N/A'}</p>
              <p><strong>Phone:</strong> ${formData.customerContactPhone || 'N/A'}</p>
              <p><strong>Email:</strong> ${formData.customerContactEmail || 'N/A'}</p>
            </div>
            <div style="flex: 1; min-width: 300px;">
              <h4 style="color: #666;">Ship To:</h4>
              <p><strong>Company:</strong> ${formData.shipToName || 'N/A'}</p>
              <p><strong>Address:</strong> ${formData.shipToAddress || 'N/A'}</p>
              <p><strong>City, State ZIP:</strong> ${[formData.shipToCity, formData.shipToState, formData.shipToZip].filter(Boolean).join(', ') || 'N/A'}</p>
              <p><strong>Contact:</strong> ${formData.shipToContactName || 'N/A'}</p>
              <p><strong>Phone:</strong> ${formData.shipToContactPhone || 'N/A'}</p>
              <p><strong>Email:</strong> ${formData.shipToContactEmail || 'N/A'}</p>
            </div>
          </div>
        </div>

        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
          <h3 style="color: #333; margin-top: 0;">Shipment Items (${shipmentItems.length})</h3>
          ${shipmentItems.length > 0 ? `
            <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
              <thead>
                <tr style="background-color: #e9ecef;">
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Title</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Description</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Quantity</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Ship Type</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Requested Date</th>
                </tr>
              </thead>
              <tbody>
                ${shipmentItems.map(item => `
                  <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item.title || 'N/A'}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item.description || 'N/A'}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item.quantity || 'N/A'}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${formatShipTypeForDisplay(item.shipType)}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${item.requestedDeliveryDate || 'N/A'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : '<p>No items specified.</p>'}
        </div>

        <div style="background-color: #e8f4fd; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #0066cc;">
          <p style="margin: 0; font-size: 14px; color: #666;">
            This Ship Early form has been submitted and is ready for processing. Please review the details above and take appropriate action according to the Ship Early procedure.
          </p>
        </div>
      </div>    `;
  };
  // Generate PDF for a single Ship Early form
  const generateShipEarlyPDF = (form) => {
    const formatCurrency = (value) => {
      if (!value) return 'N/A';
      const numValue = parseFloat(value);
      return isNaN(numValue) ? 'N/A' : `$${numValue.toFixed(2)}`;
    };

    const getBillingType = (form) => {
      const billingType = form.BillingType || form.billingType;
      const jobNumber = form.JobNumber || form.jobNumber;
      if (!billingType) return 'N/A';
      if (billingType === 'Job Number') {
        return `Job Number: ${jobNumber}`;
      }
      return billingType;
    };

    try {
      setLoading(true);
      setSaveStatus('Generating PDF...');
      setShowNotification(true);

      // Create PDF in portrait orientation for print-friendly format
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'letter'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 40;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Company header
      pdf.setFontSize(20);
      pdf.setTextColor(0, 102, 204);
      pdf.text('Watertronics', margin, yPosition);
      yPosition += 30;

      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Early or Direct Ship from Vendor Order Form', margin, yPosition);
      yPosition += 40;

      // Form date and author
      pdf.setFontSize(10);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth - margin - 100, margin);
      pdf.text(`Form ID: ${form.ID}`, pageWidth - margin - 100, margin + 15);
      yPosition += 10;

      // Job Information Section
      pdf.setFontSize(14);
      pdf.setTextColor(0, 102, 204);
      pdf.text('Job Information', margin, yPosition);
      yPosition += 20;

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      
      const jobInfo = [
        ['Job Name:', form.JobName || form.jobName || 'N/A'],
        ['Job Number:', form.JobNumber || form.jobNumber || 'N/A'],
        ['Form Author:', form.WrittenBy || form.writtenBy || 'N/A'],
        ['Cost Associated:', formatCurrency(form.ShipmentCost || form.shipmentCost)],
        ['Billing Type:', getBillingType(form)]
      ];

      if (form.InvoiceNumber || form.invoiceNumber) {
        jobInfo.push(['Invoice Number:', form.InvoiceNumber || form.invoiceNumber]);
      }

      jobInfo.forEach(([label, value]) => {
        pdf.text(label, margin, yPosition);
        pdf.text(value, margin + 120, yPosition);
        yPosition += 15;
      });

      yPosition += 10;

      // Customer Information Section
      pdf.setFontSize(14);
      pdf.setTextColor(0, 102, 204);
      pdf.text('Customer Information', margin, yPosition);
      yPosition += 25;

      // Bill To and Ship To side by side
      const leftColX = margin;
      const rightColX = margin + (contentWidth / 2);
      let leftY = yPosition;
      let rightY = yPosition;

      // Bill To
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.text('Bill To:', leftColX, leftY);
      leftY += 20;

      pdf.setFontSize(10);
      const billToInfo = [
        ['Company:', form.BillToCompany || form.billToCompany || 'N/A'],
        ['Address:', form.BillToAddress || form.billToAddress || 'N/A'],
        ['City:', form.BillToCity || form.billToCity || 'N/A'],
        ['State:', form.BillToState || form.billToState || 'N/A'],
        ['ZIP:', form.BillToZip || form.billToZip || 'N/A'],
        ['Contact:', form.BillToName || form.billToName || 'N/A'],
        ['Phone:', form.BillToPhone || form.billToPhone || 'N/A'],
        ['Email:', form.BillToEmail || form.billToEmail || 'N/A']
      ];

      billToInfo.forEach(([label, value]) => {
        pdf.text(label, leftColX, leftY);
        const text = pdf.splitTextToSize(value, 150);
        pdf.text(text, leftColX + 60, leftY);
        leftY += 15;
      });

      // Ship To
      pdf.setFontSize(12);
      pdf.text('Ship To:', rightColX, rightY);
      rightY += 20;

      pdf.setFontSize(10);
      const shipToInfo = [
        ['Company:', form.ShipToCompany || form.shipToCompany || 'N/A'],
        ['Address:', form.ShipToAddress || form.shipToAddress || 'N/A'],
        ['City:', form.ShipToCity || form.shipToCity || 'N/A'],
        ['State:', form.ShipToState || form.shipToState || 'N/A'],
        ['ZIP:', form.ShipToZip || form.shipToZip || 'N/A'],
        ['Contact:', form.ShipToName || form.shipToName || 'N/A'],
        ['Phone:', form.ShipToPhone || form.shipToPhone || 'N/A'],
        ['Email:', form.ShipToEmail || form.shipToEmail || 'N/A']
      ];

      shipToInfo.forEach(([label, value]) => {
        pdf.text(label, rightColX, rightY);
        const text = pdf.splitTextToSize(value, 150);
        pdf.text(text, rightColX + 60, rightY);
        rightY += 15;
      });

      yPosition = Math.max(leftY, rightY) + 20;

      // Shipment Items Section
      pdf.setFontSize(14);
      pdf.setTextColor(0, 102, 204);
      pdf.text(`Shipment Items (${form.items.length})`, margin, yPosition);
      yPosition += 25;

      if (form.items.length > 0) {
        // Create table data for items
        const tableHeaders = ['Title', 'Description', 'Quantity', 'Ship Type', 'Requested Date'];
        const tableData = form.items.map(item => [
          item.title || 'N/A',
          item.description || 'N/A',
          item.quantity || 'N/A',
          formatShipTypeForDisplay(item.shipType),
          item.requestedDeliveryDate || 'N/A'
        ]);

        pdf.autoTable({
          startY: yPosition,
          head: [tableHeaders],
          body: tableData,
          theme: 'striped',
          headStyles: {
            fillColor: [233, 236, 239],
            textColor: [0, 0, 0],
            fontSize: 10,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 9,
            textColor: [0, 0, 0]
          },
          columnStyles: {
            0: { cellWidth: 80 }, // Title
            1: { cellWidth: 150 }, // Description
            2: { cellWidth: 60 }, // Quantity
            3: { cellWidth: 100 }, // Ship Type
            4: { cellWidth: 80 } // Requested Date
          },
          margin: { left: margin, right: margin },
          tableWidth: 'wrap'
        });

        yPosition = pdf.lastAutoTable.finalY + 20;
      } else {
        pdf.setFontSize(10);
        pdf.setTextColor(100, 100, 100);
        pdf.text('No items specified.', margin, yPosition);
        yPosition += 20;
      }

      // Footer
      yPosition = pageHeight - 60;
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('This Ship Early form has been submitted and is ready for processing.', margin, yPosition);
      pdf.text('Please review the details above and take appropriate action according to the Ship Early procedure.', margin, yPosition + 12);

      // Generate filename
      const jobNumber = form.JobNumber || form.jobNumber || 'Unknown';
      const jobName = (form.JobName || form.jobName || 'Unknown').replace(/[^a-zA-Z0-9]/g, '_');
      const createdDate = form.createdDate ? new Date(form.createdDate).toLocaleDateString().replace(/\//g, '-') : new Date().toLocaleDateString().replace(/\//g, '-');
      const filename = `Ship_Early_Form_${jobNumber}_${jobName}_${createdDate}.pdf`;

      // Save the PDF
      pdf.save(filename);

      setSaveStatus('PDF exported successfully!');
      setTimeout(() => {
        setShowNotification(false);
      }, 2000);

      return true;
    } catch (error) {
      console.error('Error generating PDF:', error);
      setSaveStatus('Error generating PDF. Please try again.');
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Export all submitted forms as separate PDFs
  const handleExportAllPDFs = async () => {
    if (submittedForms.length === 0) {
      alert('No forms available to export.');
      return;
    }

    setLoading(true);
    setSaveStatus('Generating PDF exports...');
    setShowNotification(true);

    try {
      let successCount = 0;
      let failCount = 0;

      for (const form of submittedForms) {
        const success = generateShipEarlyPDF(form);
        if (success) {
          successCount++;
        } else {
          failCount++;
        }
        // Small delay between PDF generations to prevent browser lockup
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (failCount === 0) {
        setSaveStatus(`Successfully exported ${successCount} PDF(s).`);
      } else {
        setSaveStatus(`Exported ${successCount} PDF(s) successfully, ${failCount} failed.`);
      }

      setTimeout(() => {
        setShowNotification(false);
      }, 3000);

    } catch (error) {
      console.error('Error during PDF export:', error);
      setSaveStatus('Error occurred during PDF export.');
      setTimeout(() => {
        setShowNotification(false);
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission - sequential creation approach
  const handleSubmit = async (e) => {
    if (!hasPermission) { e.preventDefault(); return; }
    e.preventDefault();
    
    if (!window.confirm('Are you sure you want to submit? This will create the parent record and all items.')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const { parentData, items } = formatFormDataForApi();
      
      // Step 1: Create or update parent record
      let parentId = formId;
      
      if (parentId) {
        // Update existing parent
        await axiosInstance.put(`${apiUrls.shipEarlyParent}?id=${parentId}`, parentData);
      } else {
        // Create new parent
        await axiosInstance.post(apiUrls.shipEarlyParent, parentData);
        
        // Wait for database to process
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Fetch the created record to get the ID
        const fetchResponse = await axiosInstance.get(`${apiUrls.shipEarlyParent}?projectID=${jobId}`);
        
        if (fetchResponse.data && fetchResponse.data.length > 0) {
          const matchingRecords = fetchResponse.data.filter(record => 
            String(record.ProjectID) === String(jobId)
          );
          
          if (matchingRecords.length > 0) {
            matchingRecords.sort((a, b) => (b.ID || 0) - (a.ID || 0));
            parentId = matchingRecords[0].ID;            setFormId(parentId);
            setHasExistingParent(true);
          } else {
            throw new Error('Created parent record but could not find it.');
          }
        } else {
          throw new Error('Failed to create parent record.');
        }
      }      // Step 2: Create items sequentially and collect their IDs
      const createdItemIds = [];
        if (items.length > 0) {

        for (const item of items) {
          const itemData = {
            ...item,
            ParentID: parentId,
            ShipType: formatShipTypeForStorage(item.ShipType) // Format shipType for storage
          };
          
          await axiosInstance.post(apiUrls.shipEarlyItems, itemData);
          
          // Wait for database to process
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        // Fetch created items to get their IDs
        const itemsResponse = await axiosInstance.get(`${apiUrls.shipEarlyItems}?parentID=${parentId}`);
        if (itemsResponse.data && itemsResponse.data.length > 0) {
          createdItemIds.push(...itemsResponse.data.map(item => ({
            id: item.ID || item.id,
            title: item.title || item.Title || '',
            description: item.description || item.Description || '',
            quantity: item.quantity || item.Quantity || '',
            shipType: item.shipType || item.ShipType || '',
            requestedDeliveryDate: item.requestedDeliveryDate || item.RequestedDeliveryDate || ''
          })));
        }
      }        // Step 3: Create generalProject entries for each ship early item
      if (createdItemIds.length > 0) {
        for (const item of createdItemIds) {
          // Format ModelNumber as "ShipType - Title (Quantity)" (shipType is already formatted)
          const modelNumber = `${item.shipType} - ${item.title} (${item.quantity})`;
          const currentDate = new Date().toISOString().split('T')[0];
          const dollarValue = formData.costAssociated
            ? String(formData.costAssociated).replace(/[^0-9.-]/g, '')
            : '';
          const formatDateForApiCall = (dateStr) => {
            if (dateStr === null || dateStr === undefined) return null;
            const normalizedDate = String(dateStr).trim();
            if (!normalizedDate || normalizedDate === 'null' || normalizedDate === 'undefined') return null;
            return formatDateForAPI(dateStr);
          };

          const scheduleEntryData = {
            JobNumber: generalProjectData.JobNumber || formData.jobNum,
            ProjectName: generalProjectData.ProjectName || formData.jobName,
            State: generalProjectData.State || '',
            SalesRep: generalProjectData.SalesRep || '',
            ProjectManager: generalProjectData.ProjectManager || '',
            SalesQuoteID: generalProjectData.SalesQuoteID || generalProjectData.SaleQuoteID || '',
            OrderDate: formatDateForApiCall(generalProjectData.OrderDate),
            RequestedDeliveryDate: formatDateForApiCall(item.requestedDeliveryDate),
            JobLocation: '',
            ModelNumber: modelNumber,
            JobStatus: "Ready to Schedule",
            JobType: '',
            Notes: '',
            ScheduleOnly: "Yes",
            ScheduledDate: currentDate,
            SendDocumentsToSales: 'false',
            CustomControlsEngineering: 'false',
            NewConstruction: 'false',
            HasSubmittal: 'false',
            ProductionRelease: currentDate,
            DollarValue: dollarValue,
            PaymentTerms: '',
          };

          const cleanedScheduleEntryData = Object.fromEntries(
            Object.entries(scheduleEntryData).filter(([, value]) => value !== null && value !== undefined)
          );

          try {
            console.log('Creating schedule entry for item:', item.id, 'with data:', cleanedScheduleEntryData);
            const response = await dispatch(
              postData({
                url: apiUrls.generalProject,
                data: cleanedScheduleEntryData,
                contract: { requestCase: 'pascal' },
              })
            ).unwrap();
            console.log('Successfully created schedule entry for item:', item.id, 'Response:', response);
          } catch (itemError) {
            console.error(`Error creating schedule entry for item ${item.id}:`, itemError);
            console.error('Failed data:', cleanedScheduleEntryData);
            if (itemError.response) {
              console.error('Response status:', itemError.response.status);
              console.error('Response data:', itemError.response.data);
              console.error('Response headers:', itemError.response.headers);
            }
            // Log specific field values that might be causing issues
            console.error('Problematic field values:', {
              OrderDate: cleanedScheduleEntryData.OrderDate,
              RequestedDeliveryDate: cleanedScheduleEntryData.RequestedDeliveryDate,
              ScheduledDate: cleanedScheduleEntryData.ScheduledDate,
              ProductionRelease: cleanedScheduleEntryData.ProductionRelease
            });
            // Continue with other items even if one fails
          }
        }      }      // Send notification email after successful form submission
      try {
        const emailBody = formatEmailBody(formData, shipmentItems);

        if (emailRecipients.length === 0) {
          throw new Error(
            'The current user email is unavailable for the Dev/UAT Ship Early notification.'
          );
        }
        
        await sendEmail({
          subject: `Ship Early Form Submitted - Job ${formData.jobNum} (${formData.jobName})`,
          body: emailBody,
          toRecipients: emailRecipients,
          from: 'ship.early@watertronics.com'
        });
        
        console.log('Ship Early form notification email sent successfully');
        setSaveStatus(
          restrictEmailToCurrentUser
            ? 'Form and all items submitted successfully and added to schedule! Notification email sent only to the current user.'
            : 'Form and all items submitted successfully and added to schedule! Notification email sent to stakeholders.'
        );
      } catch (emailErr) {
        console.error('Failed to send Ship Early form notification email:', emailErr);
        console.error('Email error details:', emailErr.message);
        if (emailErr.response) {
          console.error('Email response status:', emailErr.response.status);
          console.error('Email response data:', emailErr.response.data);
          console.error('Email response headers:', emailErr.response.headers);
        }
        if (emailErr.stack) {
          console.error('Email error stack:', emailErr.stack);
        }
        // Don't fail the entire submission if email fails
        setSaveStatus('Form submitted successfully but email notification failed. Please notify stakeholders manually.');
      }
      
      // Only set default success message if email succeeded
      if (!saveStatus.includes('email notification failed')) {
        // setSaveStatus is already set above in the try block
      }
      setShowNotification(true);
      
      // Reset form for new submission
      setFormId(null);
      setShowNewForm(false);
      setFormSubmitted(false);
      
      // Reset form data to defaults
      setFormData({
        jobName: generalProjectData.ProjectName || '',
        jobNum: generalProjectData.JobNumber || '',
        formAuthor: currentUser || '',
        billingType: '',
        invoiceNumber: '',
        customerName: '',
        customerAddress: '',
        customerCity: '',
        customerState: '',
        customerZip: '',
        customerContactName: '',
        customerContactPhone: '',
        customerContactEmail: '',
        shipToName: '',
        shipToAddress: '',
        shipToCity: '',
        shipToState: '',
        shipToZip: '',
        shipToContactName: '',
        shipToContactPhone: '',
        shipToContactEmail: '',
        costAssociated: ''
      });
      
      // Clear current items
      setShipmentItems([]);
      
      // Refresh the list of submitted forms
      await checkExistingParent();
      
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
      
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit form. Please try again later.');
      
      setSaveStatus(`Failed to submit form: ${err.message}`);
      setShowNotification(true);
      
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  // Show loading state during initial check
  if (initialLoading) {
    return <div className="text-center p-5"><h3>Loading...</h3></div>;
  }
  
  // Show error if any
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        <button 
          className="btn btn-outline-danger ms-3"
          onClick={() => checkExistingParent()}
        >
          Try Again
        </button>
      </div>
    );
  }
    // Show "Add Ship Early" button if no existing parent found
  if (!hasExistingParent && !showNewForm) {
    return (
      <div className="container ship-early-form-page">
        <Card className="my-5 shadow-sm">
          <Card.Header className="bg-light">
            <h4>Ship Early Information</h4>
          </Card.Header>
          <Card.Body className="text-center">
            <Alert variant="info">
              <Alert.Heading>No Ship Early Form Found</Alert.Heading>
              <p>There is currently no Ship Early form for this project. {hasPermission ? 'Click the button below to create one.' : 'Contact an Admin or Apps user to create one.'}</p>
            </Alert>
            {hasPermission && (
              <Button 
                variant="primary" 
                size="lg" 
                onClick={createNewShipEarlyForm}
                disabled={loading}
              >
                Create Ship Early Form
              </Button>
            )}
          </Card.Body>
        </Card>
      </div>
    );
  }
  // Show the main form if we have an existing parent or are in edit mode
  return (
    <div className="container ship-early-form-page">
      <h1 className="text-center mb-4">Early or Direct Ship from Vendor Order Forms</h1>
      
      <SaveBanner showNotification={showNotification} saveStatus={saveStatus} />
      
      {/* List of submitted forms */}
      {submittedForms.length > 0 && (
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-light d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Submitted Ship Early Forms ({submittedForms.length})</h4>
            {/* Export all PDFs button */}
            <Button 
              variant="outline-primary"
              size="sm"
              onClick={handleExportAllPDFs}
              disabled={loading}
            >
              <i className="bi bi-file-earmark-pdf me-1"></i>
              Export All as PDF
            </Button>
          </Card.Header>
          <Card.Body className="p-0">
            {submittedForms.map((form, index) => (
              <div key={form.ID} className={`border-bottom ${index === submittedForms.length - 1 ? '' : 'border-bottom'}`}>
                <div 
                  className="p-3 cursor-pointer bg-light-hover d-flex justify-content-between align-items-center"
                  onClick={() => toggleFormExpansion(form.ID)}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                      <p>
                      Created: {new Date(form.createdDate).toLocaleDateString()} | 
                      Items: {form.items.length} | 
                      Author: {form.WrittenBy || form.writtenBy}
                      {form.ShipmentCost || form.shipmentCost ? 
                        ` | Cost: $${parseFloat(form.ShipmentCost || form.shipmentCost).toFixed(2)}` : 
                        ''
                      }
                    </p>                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="link"
                      size="sm"
                      className="p-1 text-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEditModal(form);
                      }}
                      title="Edit form"
                      disabled={loading || !hasPermission}
                    >
                      <i className="bi bi-pencil-square"></i>
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="p-1 text-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        generateShipEarlyPDF(form);
                      }}
                      title="Export this form as PDF"
                      disabled={loading}
                    >
                      <i className="bi bi-file-earmark-pdf"></i>
                    </Button>
                    <i className={`bi ${expandedFormId === form.ID ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                  </div>
                </div>
                
                {expandedFormId === form.ID && (
                  <div className="p-3 bg-light">
                    <Row>
                      <Col md={6}>
                        <h6>Form Information</h6>
                        <p><strong>Job Number:</strong> {form.JobNumber || form.jobNumber}</p>
                        <p><strong>Billing Type:</strong> {form.BillingType || form.billingType || 'N/A'}</p>
                        {(form.InvoiceNumber || form.invoiceNumber) && (
                          <p><strong>Invoice Number:</strong> {form.InvoiceNumber || form.invoiceNumber}</p>
                        )}
                      </Col>
                      <Col md={6}>
                        <h6>Customer Information</h6>
                        <p><strong>Bill To:</strong> {form.BillToCompany || form.billToCompany}</p>
                        <p><strong>Ship To:</strong> {form.ShipToCompany || form.shipToCompany}</p>
                      </Col>                    </Row>
                    
                    {/* Individual Export Button */}
                    <div className="text-center my-3">
                      <Button 
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => generateShipEarlyPDF(form)}
                        disabled={loading}
                      >
                        <i className="bi bi-file-earmark-pdf me-1"></i>
                        Export This Form as PDF
                      </Button>
                    </div>
                    
                    {form.items.length > 0 && (
                      <div className="mt-3">
                        <h6>Items ({form.items.length})</h6>
                        <Table
                          striped
                          bordered
                          hover
                          size="sm"
                          className="ship-early-submitted-items-table"
                        >
                          <thead>
                            <tr>
                              <th>Title</th>
                              <th>Description</th>
                              <th>Quantity</th>
                              <th>Ship Type</th>
                              <th>Status</th>
                              <th>Requested Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {form.items.map((item) => (
                              <tr key={item.id}>
                                <td data-label="Title">{item.title}</td>
                                <td data-label="Description">{item.description}</td>
                                <td data-label="Quantity">{item.quantity}</td>
                                <td data-label="Ship Type">{formatShipTypeForDisplay(item.shipType)}</td>
                                <td data-label="Status">{getItemJobStatus(item, form)}</td>
                                <td data-label="Requested Date">{item.requestedDeliveryDate || 'N/A'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </Card.Body>
        </Card>
      )}
      
      {/* Add new form button */}
      {!showNewForm && hasPermission && (
        <div className="text-center mb-4">
          <Button 
            variant="success" 
            size="lg" 
            onClick={createNewShipEarlyForm}
            disabled={loading}
          >
            <i className="bi bi-plus-circle me-2"></i>
            {submittedForms.length > 0 ? 'Add Another Ship Early Form' : 'Create Ship Early Form'}
          </Button>
        </div>
      )}
      
      {/* Show message for users without permission */}
      {!showNewForm && !hasPermission && submittedForms.length > 0 && (
        <Alert variant="warning" className="text-center mb-4">
          <i className="bi bi-lock-fill me-2"></i>
          You need Admin or Apps permission to create new Ship Early forms.
        </Alert>
      )}
        {/* New form section */}
      {showNewForm && (
        <>
          <Card className="mb-4 shadow-sm border-success">
            <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
              <h4 className="mb-0">New Ship Early Form</h4>
              <Button 
                variant="outline-light" 
                size="sm" 
                onClick={cancelNewForm}
                disabled={loading}
              >
                <i className="bi bi-x"></i> Cancel
              </Button>
            </Card.Header>
          </Card>
          
          <Accordion className="mb-4">
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <h4 className="mb-0">Instructions for Early or Direct Ship Equipment Order Form</h4>
              </Accordion.Header>
              <Accordion.Body>
                <p className="mb-3">
                  <strong>Definition of items that should be included in this procedure:</strong> any product or items 
                  to be shipped EARLY or DIRECT from a WT vendor; any product to be purchased for shipment to WT for 
                  value added work then shipped EARLY
                </p>
                
                <Table
                  striped
                  bordered
                  hover
                  size="sm"
                  className="ship-early-instructions-table"
                >
                  <tbody>
                <tr>
                  <td width="5%">1</td>
                  <td>Salesman is responsible to identify items for special attention.</td>
                </tr>
                <tr>
                  <td>2</td>
                  <td>Early/Direct Form's Cover Page to be filled out by WT Apps/Sales person. The Early/Direct Form, and any associated documentation, shall be kept in the Job folder Job#/JOB TRACKING. File name example: SHIP EARLY Form 2023390 Raindance - 11-28-2023</td>
                </tr>
                <tr>
                  <td>3</td>
                  <td>Apps/Sales will confirm that all necessary addresses and site contacts and phone numbers are included on Cover Page.</td>
                </tr>
                <tr>
                  <td>4</td>
                  <td>Apps/Sales will provide exact part numbers and descriptions of materials involved, wherever practical. (example: 41-64-1010, 10" BFV, and all necessary hardware)</td>
                </tr>
                <tr>
                  <td>5</td>
                  <td>Apps/Sales will confirm with Accounting the status of Down Payment receipt, prior to submitting the Early/Direct Form. If Down Payment has not been received, approval for Early/Direct Form release must come from Executive Management.</td>
                </tr>
                <tr>
                  <td>6</td>
                  <td>Apps to define documentation, quotes, and other information that needs to be attached the Early/Direct Form.</td>
                </tr>
                <tr>
                  <td>7</td>
                  <td>Apps to send Early/Direct Form, and any other relevant data, to Master Scheduler; copy Salesman, Purchasing Director, and other key stakeholders.</td>
                </tr>
                <tr>
                  <td>8</td>
                  <td>Master Scheduler chooses/assigns Design Engineer and schedules due date, then forwards email from Apps to assigned DE, copying same group on original email and Apps.</td>
                </tr>
                <tr>
                  <td>9</td>
                  <td>Design Engineer will create a folder Job#/MECHANICAL/DRAWINGS-BOM/SHIP EARLY and copy the Early/Direct Form Excel file into Job#/MECHANICAL/DRAWINGS-BOM/SHIP EARLY.</td>
                </tr>
                <tr>
                  <td>10</td>
                  <td>Design Engineer will save and rename the Early/Direct Form Excel file into Job#/MECHANICAL/DRAWINGS-BOM/SHIP EARLY as file name SHIP EARLY Job# JobName - Rev or SHIP DIRECT Job# JobName - Rev. Example: SHIP EARLY 2023390 Raindance - Rev 11-28-2023</td>
                </tr>
                <tr>
                  <td>11</td>
                  <td>Design Engineer will complete the SHIP EARLY-DIRECT BOM tab in this file and include any other dwgs/docs into Engineering folder.</td>
                </tr>
                <tr>
                  <td>12</td>
                  <td>Design Engineer will locate the projects released production order in NAV, and upload all items from the SHIP EARLY-DIRECT BOM tab into the component lines. The engineer needs to enter the Due Date in the corresponding column for all items and change the routing link to SHIP EARLY. This date is the scheduled fabrication date or the assembly date if there is no fabrication to be done.</td>
                </tr>
                <tr>
                  <td>13</td>
                  <td>Design Engineer deliver to Purchasing a printed packet Cover Page and BOM Page, and any applicable dwgs requiring Fabrication and/or Assembly.</td>
                </tr>
                <tr>
                  <td>14</td>
                  <td>Design Engineer will follow up by sending Early/Direct Form with completed BOM and applicable drwgs/docs via email to Master Scheduler, copying all key stakeholders, such as: PSN Coordinator, Fabrication Supervisor, Operations Manager, Production Manager, Purchasing Manager, Lead Buyer, Material Handling, Shipping Supervisor, Salesman, Applications Engineer, Director of Engineering and other applicable personnel.</td>
                </tr>
                <tr>
                  <td>15</td>
                  <td>Purchasing will update item cards with the correct lead times. If items will not arrive before the due date entered by the design engineer, purchasing is to inform the scheduler of the potential delay.</td>
                </tr>
              </tbody>
            </Table>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Form onSubmit={handleSubmit} className="ship-early-responsive-form">
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-light">
            <h4>Job Information</h4>
          </Card.Header>
          <Card.Body>
            <Row className="ship-early-two-column-row">
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Job Name:</Form.Label>
                  <Form.Control
                    type="text"
                    name="jobName"
                    value={formData.jobName}
                    onChange={handleChange}
                    disabled
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Job Number:</Form.Label>
                  <Form.Control
                    type="text"
                    name="jobNum"
                    value={formData.jobNum}
                    onChange={handleChange}
                    disabled
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Form Author:</Form.Label>
                  <Form.Control
                    type="text"
                    name="formAuthor"
                    value={formData.formAuthor}
                    onChange={handleChange}
                    disabled
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-light">
            <h4>Shipment Items</h4>
          </Card.Header>
          <Card.Body>
            {shipmentItems.length === 0 ? (
              <div className="text-center text-muted py-4">
                <p>No shipment items added. Click the button below to add items.</p>
              </div>
            ) : (              shipmentItems.map((item, index) => (
                <Row key={item.id} className="mb-3 mx-0 ship-early-line-item" style={{
                  border: "1px solid #dee2e6", 
                  padding: "15px", 
                  borderRadius: "8px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)" 
                }}>
                  <Col md={11} className="ship-early-line-item-content">
                    <Row className="ship-early-line-item-primary">
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label>Title</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.title}
                            onChange={(e) => handleItemChange(item.id, 'title', e.target.value)}
                            placeholder="Enter item title"
                            disabled={formSubmitted && !item.isNew}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-2">
                          <Form.Label>Quantity</Form.Label>
                          <Form.Control
                            type="text"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(item.id, 'quantity', e.target.value)}
                            placeholder="Enter quantity"
                            disabled={formSubmitted && !item.isNew}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-2">
                          <Form.Label>Ship Type</Form.Label>
                          <Form.Select
                            value={item.shipType}
                            onChange={(e) => handleItemChange(item.id, 'shipType', e.target.value)}
                            disabled={formSubmitted && !item.isNew}
                          >
                            <option value="">Select Ship Type...</option>
                            <option value="shipEarly">Ship Early</option>
                            <option value="shipDirect">Ship Early & Direct</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="ship-early-line-item-secondary">
                      <Col md={8}>
                        <Form.Group className="mb-2">
                          <Form.Label>Description</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={item.description}
                            onChange={(e) => handleItemChange(item.id, 'description', e.target.value)}
                            placeholder="Enter item description"
                            disabled={formSubmitted && !item.isNew}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-2">
                          <Form.Label>Requested Delivery Date</Form.Label>
                          <Form.Control
                            type="date"
                            value={item.requestedDeliveryDate}
                            onChange={(e) => handleItemChange(item.id, 'requestedDeliveryDate', e.target.value)}
                            disabled={formSubmitted && !item.isNew}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Col>
                  <Col md={1} className="d-flex align-items-center justify-content-center ship-early-line-item-actions">
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleRemoveItem(item.id)}
                      title="Remove item"
                      disabled={formSubmitted && !item.isNew}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>                  </Col>
                </Row>
              ))
            )}            <div className="d-grid gap-2 col-md-4 mx-auto mt-3 ship-early-add-item">
              <Button 
                variant="outline-primary" 
                onClick={handleAddItem}
                disabled={loading || formSubmitted}
              >
                <i className="bi bi-plus-circle me-2"></i>Add Item
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-light">
            <h4>Cost & Billing Information</h4>
          </Card.Header>
          <Card.Body>
            <Row className="ship-early-two-column-row">
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Cost Associated With Shipment:</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text">$</span>                    <Form.Control
                      type="text"
                      name="costAssociated"
                      value={formData.costAssociated}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="0.00"
                      disabled={formSubmitted}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Label>Billing Information:</Form.Label>
                <div>
                  <Form.Group className="mb-2">
                    <Form.Check
                      type="radio"
                      name="billingType"
                      id="newInvoice"
                      label="New Invoice"
                      checked={formData.billingType === 'New Invoice'}
                      onChange={() => handleBillingTypeChange('New Invoice')}
                      disabled={formSubmitted}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Check
                      type="radio"
                      name="billingType"
                      id="applyToInvoice"
                      label="Apply to Invoice"
                      checked={formData.billingType === 'Apply to Invoice'}
                      onChange={() => handleBillingTypeChange('Apply to Invoice')}
                      disabled={formSubmitted}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Check
                      type="radio"
                      name="billingType"
                      id="jobNumber"
                      label="Job Number"
                      checked={formData.billingType === 'Job Number'}
                      onChange={() => handleBillingTypeChange('Job Number')}
                      disabled={formSubmitted}
                    />
                  </Form.Group>
                </div>
                {(formData.billingType === 'New Invoice' || formData.billingType === 'Apply to Invoice') && (
                  <Form.Group className="mb-3">
                    <Form.Label>Invoice Number:</Form.Label>
                    <Form.Control
                      type="text"
                      name="invoiceNumber"
                      value={formData.invoiceNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter invoice number"
                      disabled={formSubmitted}
                    />
                  </Form.Group>
                )}
                {formData.billingType === 'Job Number' && (
                  <Form.Group className="mb-3">
                    <Form.Label>Job Number (auto-filled):</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.jobNum}
                      disabled
                      readOnly
                    />
                  </Form.Group>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-light">
            <h4>Customer Information</h4>
          </Card.Header>
          <Card.Body>
            <Row className="ship-early-address-columns">
              <Col md={6}>
                <Card className="h-100 border-light">
                  <Card.Body>
                    <h5 className="card-title border-bottom pb-2">Customer / Bill To</h5>
                    <Form.Group className="mb-2">
                      <Form.Label>Name:</Form.Label>
                      <Form.Control
                        type="text"
                        name="customerName"
                        value={formData.customerName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Customer name"
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Address:</Form.Label>
                      <Form.Control
                        type="text"
                        name="customerAddress"
                        value={formData.customerAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Street address"
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                    <Row className="ship-early-locality-row">                      <Col>
                        <Form.Group className="mb-2">
                          <Form.Label>City:</Form.Label>
                          <Form.Control
                            type="text"
                            name="customerCity"
                            value={formData.customerCity}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={formSubmitted}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className="mb-2">
                          <Form.Label>State:</Form.Label>
                          <Form.Control
                            type="text"
                            name="customerState"
                            value={formData.customerState}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={formSubmitted}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className="mb-2">
                          <Form.Label>ZIP:</Form.Label>
                          <Form.Control
                            type="text"
                            name="customerZip"
                            value={formData.customerZip}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={formSubmitted}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <hr className="my-3" />
                    <h6 className="mb-2">Contact Information</h6>                    <Form.Group className="mb-2">
                      <Form.Label>Name:</Form.Label>
                      <Form.Control
                        type="text"
                        name="customerContactName"
                        value={formData.customerContactName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Phone:</Form.Label>
                      <Form.Control
                        type="text"
                        name="customerContactPhone"
                        value={formData.customerContactPhone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Email:</Form.Label>
                      <Form.Control
                        type="email"
                        name="customerContactEmail"
                        value={formData.customerContactEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="h-100 border-light">
                  <Card.Body>
                    <h5 className="card-title border-bottom pb-2">Ship To</h5>                    <Form.Group className="mb-2">
                      <Form.Label>Name:</Form.Label>
                      <Form.Control
                        type="text"
                        name="shipToName"
                        value={formData.shipToName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Ship to name"
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Address:</Form.Label>
                      <Form.Control
                        type="text"
                        name="shipToAddress"
                        value={formData.shipToAddress}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Street address"
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                    <Row className="ship-early-locality-row">                      <Col>
                        <Form.Group className="mb-2">
                          <Form.Label>City:</Form.Label>
                          <Form.Control
                            type="text"
                            name="shipToCity"
                            value={formData.shipToCity}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={formSubmitted}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className="mb-2">
                          <Form.Label>State:</Form.Label>
                          <Form.Control
                            type="text"
                            name="shipToState"
                            value={formData.shipToState}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={formSubmitted}
                          />
                        </Form.Group>
                      </Col>
                      <Col>
                        <Form.Group className="mb-2">
                          <Form.Label>ZIP:</Form.Label>
                          <Form.Control
                            type="text"
                            name="shipToZip"
                            value={formData.shipToZip}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            disabled={formSubmitted}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <hr className="my-3" />
                    <h6 className="mb-2">Contact Information</h6>                    <Form.Group className="mb-2">
                      <Form.Label>Name:</Form.Label>
                      <Form.Control
                        type="text"
                        name="shipToContactName"
                        value={formData.shipToContactName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Phone:</Form.Label>
                      <Form.Control
                        type="text"
                        name="shipToContactPhone"
                        value={formData.shipToContactPhone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label>Email:</Form.Label>
                      <Form.Control
                        type="email"
                        name="shipToContactEmail"
                        value={formData.shipToContactEmail}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={formSubmitted}
                      />
                    </Form.Group>
                  </Card.Body>
                </Card>
              </Col>
            </Row>          </Card.Body>
        </Card>        {/* Email Recipients Section */}
        <Card className="mb-4 shadow-sm">
          <Card.Header className="bg-light">
            <h4>Email Recipients</h4>
          </Card.Header>
          <Card.Body>
            {restrictEmailToCurrentUser ? (
              <div>
                <Alert variant="info" className="mb-3">
                  Dev/UAT safety mode is active. Email notifications will be sent only to the
                  current user.
                </Alert>
                <div className="bg-light p-3 rounded">
                  <p className="mb-0">
                    <strong>Current User:</strong>{' '}
                    {currentUserEmail || 'Email unavailable'}
                  </p>
                </div>
              </div>
            ) : contactsLoading ? (
              <div className="text-center text-muted py-3">
                <i className="bi bi-spinner-border me-2"></i>
                Loading email recipients...
              </div>
            ) : contactsError ? (
              <Alert variant="warning">
                <i className="bi bi-exclamation-triangle me-2"></i>
                Unable to load email recipient information: {contactsError}
              </Alert>            ) : (
              <div>
                <p className="text-muted mb-3">
                  The following recipients will receive email notifications:
                </p>
                <div className="bg-light p-3 rounded">
                  <p className="mb-2">
                    <strong>Default Recipients:</strong>{' '}
                    {DEFAULT_EMAIL_RECIPIENTS.join(', ')}
                  </p>
                  {projectTeamMembers.length > 0 && (
                    <p className="mb-0">
                      <strong>Project Team:</strong> {projectTeamMembers.map(member => member.email).join(', ')}
                    </p>
                  )}
                  {projectTeamMembers.length === 0 && (
                    <p className="mb-0 text-muted">
                      <em>No additional project team members found for this job.</em>
                    </p>
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>

        {!formSubmitted && (
          <div className="text-center mb-4">
            {shipmentItems.length === 0 && (
              <Alert variant="info" className="mb-3">
                <Alert.Heading>Ready to Submit?</Alert.Heading>
                <p className="mb-0">Add at least one shipment item above, then click Submit to create the form and all items.</p>
              </Alert>
            )}
            <Button 
              type="submit" 
              variant="primary" 
              size="lg"
              disabled={loading || shipmentItems.length === 0}
            >
              <i className="bi bi-check-circle me-2"></i>
              {loading ? 'Submitting...' : shipmentItems.length === 0 ? 'Add Items to Submit' : 'Submit'}
            </Button>
          </div>        )}
      </Form>
        </>
      )}

      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="xl"
        backdrop="static"
        className="ship-early-edit-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Ship Early Form</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingFormData && (
            <>
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <h5 className="mb-0">Form Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="ship-early-two-column-row">
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label>Job Name</Form.Label>
                        <Form.Control name="jobName" value={editingFormData.jobName} onChange={handleEditFormChange} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label>Job Number</Form.Label>
                        <Form.Control name="jobNum" value={editingFormData.jobNum} onChange={handleEditFormChange} />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-2">
                        <Form.Label>Form Author</Form.Label>
                        <Form.Control name="formAuthor" value={editingFormData.formAuthor} onChange={handleEditFormChange} />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mt-2 ship-early-two-column-row">
                    <Col md={6}>
                      <Form.Group className="mb-2">
                        <Form.Label>Cost Associated With Shipment</Form.Label>
                        <div className="input-group">
                          <span className="input-group-text">$</span>
                          <Form.Control
                            name="costAssociated"
                            value={editingFormData.costAssociated}
                            onChange={handleEditFormChange}
                            placeholder="0.00"
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Label>Billing Information</Form.Label>
                      <Form.Check
                        type="radio"
                        name="editBillingType"
                        label="New Invoice"
                        checked={editingFormData.billingType === 'New Invoice'}
                        onChange={() => handleEditBillingTypeChange('New Invoice')}
                      />
                      <Form.Check
                        type="radio"
                        name="editBillingType"
                        label="Apply to Invoice"
                        checked={editingFormData.billingType === 'Apply to Invoice'}
                        onChange={() => handleEditBillingTypeChange('Apply to Invoice')}
                      />
                      <Form.Check
                        type="radio"
                        name="editBillingType"
                        label="Job Number"
                        checked={editingFormData.billingType === 'Job Number'}
                        onChange={() => handleEditBillingTypeChange('Job Number')}
                      />
                      {(editingFormData.billingType === 'New Invoice' || editingFormData.billingType === 'Apply to Invoice') && (
                        <Form.Control
                          className="mt-2"
                          name="invoiceNumber"
                          value={editingFormData.invoiceNumber}
                          onChange={handleEditFormChange}
                          placeholder="Invoice Number"
                        />
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <h5 className="mb-0">Customer Information</h5>
                </Card.Header>
                <Card.Body>
                  <Row className="ship-early-address-columns">
                    <Col md={6}>
                      <h6>Customer / Bill To</h6>
                      <Form.Group className="mb-2"><Form.Label>Name</Form.Label><Form.Control name="customerName" value={editingFormData.customerName} onChange={handleEditFormChange} /></Form.Group>
                      <Form.Group className="mb-2"><Form.Label>Address</Form.Label><Form.Control name="customerAddress" value={editingFormData.customerAddress} onChange={handleEditFormChange} /></Form.Group>
                      <Row className="ship-early-locality-row">
                        <Col><Form.Group className="mb-2"><Form.Label>City</Form.Label><Form.Control name="customerCity" value={editingFormData.customerCity} onChange={handleEditFormChange} /></Form.Group></Col>
                        <Col><Form.Group className="mb-2"><Form.Label>State</Form.Label><Form.Control name="customerState" value={editingFormData.customerState} onChange={handleEditFormChange} /></Form.Group></Col>
                        <Col><Form.Group className="mb-2"><Form.Label>ZIP</Form.Label><Form.Control name="customerZip" value={editingFormData.customerZip} onChange={handleEditFormChange} /></Form.Group></Col>
                      </Row>
                      <Form.Group className="mb-2"><Form.Label>Contact Name</Form.Label><Form.Control name="customerContactName" value={editingFormData.customerContactName} onChange={handleEditFormChange} /></Form.Group>
                      <Form.Group className="mb-2"><Form.Label>Contact Phone</Form.Label><Form.Control name="customerContactPhone" value={editingFormData.customerContactPhone} onChange={handleEditFormChange} /></Form.Group>
                      <Form.Group className="mb-2"><Form.Label>Contact Email</Form.Label><Form.Control name="customerContactEmail" value={editingFormData.customerContactEmail} onChange={handleEditFormChange} /></Form.Group>
                    </Col>
                    <Col md={6}>
                      <h6>Ship To</h6>
                      <Form.Group className="mb-2"><Form.Label>Name</Form.Label><Form.Control name="shipToName" value={editingFormData.shipToName} onChange={handleEditFormChange} /></Form.Group>
                      <Form.Group className="mb-2"><Form.Label>Address</Form.Label><Form.Control name="shipToAddress" value={editingFormData.shipToAddress} onChange={handleEditFormChange} /></Form.Group>
                      <Row className="ship-early-locality-row">
                        <Col><Form.Group className="mb-2"><Form.Label>City</Form.Label><Form.Control name="shipToCity" value={editingFormData.shipToCity} onChange={handleEditFormChange} /></Form.Group></Col>
                        <Col><Form.Group className="mb-2"><Form.Label>State</Form.Label><Form.Control name="shipToState" value={editingFormData.shipToState} onChange={handleEditFormChange} /></Form.Group></Col>
                        <Col><Form.Group className="mb-2"><Form.Label>ZIP</Form.Label><Form.Control name="shipToZip" value={editingFormData.shipToZip} onChange={handleEditFormChange} /></Form.Group></Col>
                      </Row>
                      <Form.Group className="mb-2"><Form.Label>Contact Name</Form.Label><Form.Control name="shipToContactName" value={editingFormData.shipToContactName} onChange={handleEditFormChange} /></Form.Group>
                      <Form.Group className="mb-2"><Form.Label>Contact Phone</Form.Label><Form.Control name="shipToContactPhone" value={editingFormData.shipToContactPhone} onChange={handleEditFormChange} /></Form.Group>
                      <Form.Group className="mb-2"><Form.Label>Contact Email</Form.Label><Form.Control name="shipToContactEmail" value={editingFormData.shipToContactEmail} onChange={handleEditFormChange} /></Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card>
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Line Items</h5>
                  <Button variant="outline-primary" size="sm" onClick={handleAddEditItem}>
                    <i className="bi bi-plus-circle me-1"></i>Add Item
                  </Button>
                </Card.Header>
                <Card.Body>
                  {editingItems.length === 0 && <p className="text-muted mb-0">No line items.</p>}
                  {editingItems.map((item) => (
                    <Row key={item.id} className="mb-3 mx-0 ship-early-line-item" style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px' }}>
                      <Col md={11} className="ship-early-line-item-content">
                        <Row className="ship-early-line-item-primary ship-early-edit-item-fields">
                          <Col md={3}><Form.Group className="mb-2"><Form.Label>Title</Form.Label><Form.Control value={item.title} onChange={(e) => handleEditItemChange(item.id, 'title', e.target.value)} /></Form.Group></Col>
                          <Col md={2}><Form.Group className="mb-2"><Form.Label>Quantity</Form.Label><Form.Control value={item.quantity} onChange={(e) => handleEditItemChange(item.id, 'quantity', e.target.value)} /></Form.Group></Col>
                          <Col md={3}><Form.Group className="mb-2"><Form.Label>Ship Type</Form.Label><Form.Select value={item.shipType} onChange={(e) => handleEditItemChange(item.id, 'shipType', e.target.value)}><option value="">Select Ship Type...</option><option value="shipEarly">Ship Early</option><option value="shipDirect">Ship Early & Direct</option></Form.Select></Form.Group></Col>
                          <Col md={2}><Form.Group className="mb-2"><Form.Label>Job Status</Form.Label><Form.Control value={getItemJobStatus(item, editingFormData)} readOnly plaintext /></Form.Group></Col>
                          <Col md={2}><Form.Group className="mb-2"><Form.Label>Requested Date</Form.Label><Form.Control type="date" value={item.requestedDeliveryDate} onChange={(e) => handleEditItemChange(item.id, 'requestedDeliveryDate', e.target.value)} /></Form.Group></Col>
                        </Row>
                        <Form.Group className="mb-1"><Form.Label>Description</Form.Label><Form.Control as="textarea" rows={2} value={item.description} onChange={(e) => handleEditItemChange(item.id, 'description', e.target.value)} /></Form.Group>
                      </Col>
                      <Col md={1} className="d-flex justify-content-center align-items-center ship-early-line-item-actions">
                        <Button variant="outline-danger" size="sm" onClick={() => handleRemoveEditItem(item.id)}>
                          <i className="bi bi-trash"></i>
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal} disabled={savingEdit}>Cancel</Button>
          <Button variant="primary" onClick={handleSaveEditedForm} disabled={savingEdit || !editingFormData}>
            {savingEdit ? 'Saving...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ShipEarlyForm;
